import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ReportExportRequest, SupplierStatementKind, SupplierStatementReport } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/States';
import { TableSkeleton } from '@/components/Table';
import {
  fetchStatementSuppliers,
  fetchSupplierStatement,
  supplierStatementKeys,
} from '@/features/reports/supplier-statement-api';
import { downloadReportExport, type ExportFormat } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';
import {
  ledgerExportTable,
  LedgerTable,
  PartyPicker,
  StatementHeading,
  StatementPrintStyles,
  StatementSummary,
  useDebounced,
  usePartyParam,
} from './CustomerStatementPage';

const KIND_LABELS: Record<SupplierStatementKind, string> = {
  credit_purchase: 'Credit purchase',
  cash_purchase: 'Purchase',
  payment: 'Payment made',
  debit_note: 'Debit note',
  debit_note_refund: 'Debit note',
  purchase_return: 'Purchase return',
  other: 'Adjustment',
};

function supplierStatementTable(report: SupplierStatementReport): ReportExportRequest {
  return ledgerExportTable({
    title: 'Supplier Statement',
    partyName: report.supplier.name,
    branchName: report.supplier.branchName,
    from: report.from,
    to: report.to,
    debitLabel: 'Debit',
    creditLabel: 'Credit',
    opening: report.openingBalance,
    lines: report.lines.map((line) => ({
      date: line.date,
      ref: line.ref,
      description: [KIND_LABELS[line.kind], line.note, line.settled ? 'settled' : ''].filter(Boolean).join(' · '),
      debit: line.debit,
      credit: line.credit,
      balance: line.balance,
    })),
    totalDebit: report.totalDebit,
    totalCredit: report.totalCredit,
    closing: report.closingBalance,
    notes: [
      'Positive balance = owed to the supplier. Cash purchases, purchase returns and refunded debit notes are ' +
        'settled on the spot (equal debit and credit), so they do not move the balance.',
    ],
  });
}

function SupplierStatementView({ report }: { report: SupplierStatementReport }) {
  const { supplier } = report;
  return (
    <div className="statement-print rounded-lg border border-slate-200 bg-white p-6">
      <StatementHeading
        shopName={report.shopName}
        title="Supplier statement"
        partyName={supplier.name}
        details={[
          supplier.contactPerson,
          supplier.phone,
          supplier.email,
          supplier.address,
          supplier.branchName ? `Branch: ${supplier.branchName}` : '',
        ]}
        from={report.from}
        to={report.to}
      />
      <StatementSummary
        items={[
          { label: 'Opening balance', value: report.openingBalance },
          { label: 'Purchased', value: report.totalDebit },
          { label: 'Paid / returned', value: report.totalCredit },
          { label: 'Closing balance', value: report.closingBalance },
        ]}
      />
      <LedgerTable
        caption="Supplier account"
        debitLabel="Debit"
        creditLabel="Credit"
        opening={report.openingBalance}
        totalDebit={report.totalDebit}
        totalCredit={report.totalCredit}
        closing={report.closingBalance}
        rows={report.lines.map((line, index) => ({
          key: `${index}`,
          date: line.date,
          ref: line.ref,
          muted: line.settled,
          description: (
            <>
              <span className="font-medium">{KIND_LABELS[line.kind]}</span>
              {line.note ? <span className="text-slate-500"> · {line.note}</span> : null}
              {line.settled ? <span className="text-slate-500"> · settled</span> : null}
            </>
          ),
          debit: line.debit,
          credit: line.credit,
          balance: line.balance,
        }))}
      />
      <p className="mt-2 text-xs text-slate-500">
        Positive balance = owed to the supplier. Cash purchases, purchase returns and refunded debit notes are
        settled on the spot (equal debit and credit), so they do not move the balance.
      </p>
    </div>
  );
}

export function SupplierStatementPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [party, setParty] = usePartyParam('supplier');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  const suppliersQuery = useQuery({
    queryKey: supplierStatementKeys.suppliers(filters.branchId, debouncedSearch.trim()),
    queryFn: ({ signal }) => fetchStatementSuppliers(filters.branchId, debouncedSearch.trim(), signal),
  });

  const statementQuery = useQuery({
    queryKey: supplierStatementKeys.statement(party, filters.from, filters.to),
    queryFn: ({ signal }) => fetchSupplierStatement(party!, { from: filters.from, to: filters.to }, signal),
    enabled: party !== null,
  });

  return (
    <div>
      <StatementPrintStyles />
      <PageHeader
        title="Supplier statement"
        description="One supplier's account: balance brought forward, purchases, payments, returns and debit notes in the period with a running balance."
        actions={
          <>
            {statementQuery.data ? (
              <ExportMenu
                onExport={(format: ExportFormat) => downloadReportExport(supplierStatementTable(statementQuery.data), format)}
              />
            ) : null}
            <Button variant="primary" disabled={!statementQuery.data} onClick={() => window.print()}>
              Print
            </Button>
          </>
        }
      />
      <div className="print:hidden">
        <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} />
      </div>
      <PartyPicker
        label="Supplier"
        placeholder="Search by name, phone or contact"
        search={search}
        onSearch={setSearch}
        options={suppliersQuery.data ?? []}
        loading={suppliersQuery.isPending}
        selected={party}
        selectedLabel={statementQuery.data?.supplier.name ?? ''}
        onSelect={setParty}
      />

      {party === null ? (
        <EmptyState title="Pick a supplier" description="Search above, then choose a supplier to see their statement." />
      ) : (
        <AsyncSection
          state={statementQuery}
          skeleton={<TableSkeleton columns={6} />}
          errorTitle="Could not load the statement"
        >
          {(report) => <SupplierStatementView report={report} />}
        </AsyncSection>
      )}
    </div>
  );
}
